import { FC, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Button,
    CircularProgress,
    Alert,
    IconButton,
    Grid,
    Divider,
} from '@mui/material';
import {
    ArrowBack,
    Edit,
    Delete,
    Visibility as VisibilityIcon,
    VisibilityOff,
    Refresh,
    MenuBook,
    CloudUpload,
} from '@mui/icons-material';
import { LibraryItemType, LibraryTier, LIBRARY_TIER_LABELS, GroupWordbookInfo, PreviewWord } from '../../types/library';
import {useDeleteLibrary, useLibrary, useRefreshStats, useToggleVisibility, useUpdateLibrary} from "../../hooks/useLibrary.ts";
import { useUpdateWordbook } from "../../hooks/useWordbook.ts";
import { ImageUpload } from '../common/ImageUpload';
import { uploadApi } from '../../api/uploadApi';
import { formatKstDateTime } from '../../utils/dateTime';

export const LibraryShow: FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const libraryId = parseInt(id || '0', 10);

    // 단어장 썸네일 업로드 상태
    const [uploadingWordbookId, setUploadingWordbookId] = useState<number | null>(null);

    // 데이터 조회
    const { data: library, isLoading, error, refetch } = useLibrary(libraryId);

    // Mutations
    const deleteLibrary = useDeleteLibrary();
    const toggleVisibility = useToggleVisibility();
    const refreshStats = useRefreshStats();
    const updateLibrary = useUpdateLibrary();
    const updateWordbook = useUpdateWordbook();

    // 라이브러리 썸네일 업데이트
    const handleLibraryThumbnailChange = (url: string | undefined) => {
        updateLibrary.mutate({
            id: libraryId,
            data: { thumbnailUrl: url || '' }
        });
    };

    // 단어장 썸네일 업로드
    const handleWordbookThumbnailUpload = async (wordbookId: number, file: File) => {
        try {
            setUploadingWordbookId(wordbookId);
            const response = await uploadApi.uploadImage(file, 'wordbook');
            await updateWordbook.mutateAsync({
                id: wordbookId,
                data: { thumbnailUrl: response.fileUrl }
            });
            // 라이브러리 상세 갱신
            refetch();
        } catch (error) {
            console.error('Wordbook thumbnail upload error:', error);
            alert('이미지 업로드 실패');
        } finally {
            setUploadingWordbookId(null);
        }
    };

    const handleDelete = async () => {
        if (!confirm('정말 삭제하시겠습니까?')) return;

        try {
            await deleteLibrary.mutateAsync(libraryId);
            navigate('/library');
        } catch (error) {
            alert(`삭제 실패: ${(error as Error).message}`);
        }
    };

    const handleToggleVisibility = () => {
        toggleVisibility.mutate(libraryId);
    };

    const handleRefreshStats = () => {
        refreshStats.mutate(libraryId);
    };

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
                <CircularProgress />
            </Box>
        );
    }

    if (error || !library) {
        return (
            <Box p={3}>
                <Alert severity="error">
                    {error?.message || '라이브러리를 불러올 수 없습니다.'}
                </Alert>
                <Button
                    startIcon={<ArrowBack />}
                    onClick={() => navigate('/library')}
                    sx={{ mt: 2 }}
                >
                    목록으로
                </Button>
            </Box>
        );
    }

    return (
        <Box p={3}>
            {/* 헤더 */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Box display="flex" alignItems="center" gap={2}>
                    <IconButton onClick={() => navigate('/library')}>
                        <ArrowBack />
                    </IconButton>
                    <Typography variant="h4">라이브러리 상세: {library.title}</Typography>
                </Box>

                <Box display="flex" gap={1}>
                    {library.itemType === LibraryItemType.GROUP && (
                        <Button
                            startIcon={<Refresh />}
                            onClick={handleRefreshStats}
                            disabled={refreshStats.isPending}
                        >
                            통계 갱신
                        </Button>
                    )}
                    <IconButton
                        onClick={handleToggleVisibility}
                        color={library.isVisible ? 'primary' : 'default'}
                        disabled={toggleVisibility.isPending}
                    >
                        {library.isVisible ? <VisibilityIcon /> : <VisibilityOff />}
                    </IconButton>
                    <Button
                        startIcon={<Edit />}
                        variant="outlined"
                        onClick={() => navigate(`/library/${libraryId}/edit`)}
                    >
                        수정
                    </Button>
                    <Button
                        startIcon={<Delete />}
                        variant="outlined"
                        color="error"
                        onClick={handleDelete}
                        disabled={deleteLibrary.isPending}
                    >
                        삭제
                    </Button>
                </Box>
            </Box>

            <Grid container spacing={3}>
                {/* 기본 정보 */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                기본 정보
                            </Typography>
                            <Divider sx={{ mb: 2 }} />

                            {/* 썸네일 이미지 섹션 */}
                            <Box mb={2}>
                                <ImageUpload
                                    value={library.thumbnailUrl}
                                    onChange={handleLibraryThumbnailChange}
                                    folder="library"
                                    label="썸네일 이미지"
                                />
                            </Box>

                            <Box display="flex" flexDirection="column" gap={2}>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        제목
                                    </Typography>
                                    <Typography variant="body1">{library.title}</Typography>
                                </Box>

                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        설명
                                    </Typography>
                                    <Typography variant="body1">
                                        {library.description || '-'}
                                    </Typography>
                                </Box>

                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        언어 설정
                                    </Typography>
                                    <Box display="flex" gap={1} mt={0.5}>
                                        <Chip
                                            label={`단어: ${library.headLang.toUpperCase()}`}
                                            color="primary"
                                            size="small"
                                        />
                                        <Chip
                                            label={`의미: ${library.meaningLang.toUpperCase()}`}
                                            color="secondary"
                                            size="small"
                                        />
                                    </Box>
                                </Box>

                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        타입
                                    </Typography>
                                    <Box mt={0.5}>
                                        <Chip
                                            label={library.itemType === LibraryItemType.GROUP ? '그룹' : '단일'}
                                            color={library.itemType === LibraryItemType.GROUP ? 'primary' : 'default'}
                                        />
                                    </Box>
                                </Box>

                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        카테고리 섹션
                                    </Typography>
                                    <Typography variant="body1">
                                        {library.categorySection?.displayName ??
                                            library.categorySection?.code ??
                                            '-'}
                                    </Typography>
                                </Box>

                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        큐레이션 섹션
                                    </Typography>
                                    <Typography variant="body1">
                                        {library.curationSection
                                            ? `${library.curationSection.displayName ?? library.curationSection.code}${
                                                  library.curationSortOrder != null
                                                      ? ` (#${library.curationSortOrder})`
                                                      : ''
                                              }`
                                            : '-'}
                                    </Typography>
                                </Box>

                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        등급
                                    </Typography>
                                    <Box mt={0.5}>
                                        <Chip
                                            label={LIBRARY_TIER_LABELS[library.tier as LibraryTier]}
                                            color={library.tier === LibraryTier.PREMIUM ? 'warning' : 'default'}
                                            size="small"
                                        />
                                    </Box>
                                </Box>

                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        라이브러리 타입
                                    </Typography>
                                    <Typography variant="body1">{library.libraryType}</Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* 통계 */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                통계
                            </Typography>

                            <Box display="flex" flexDirection="column" gap={2}>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        단어 수
                                    </Typography>
                                    <Typography variant="h6">
                                        {library.wordCount.toLocaleString()} 단어
                                    </Typography>
                                </Box>

                                {library.itemType === LibraryItemType.GROUP && (
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">
                                            단어장 수
                                        </Typography>
                                        <Typography variant="h6">
                                            {library.wordbookCount} 권
                                        </Typography>
                                    </Box>
                                )}

                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        다운로드 수
                                    </Typography>
                                    <Typography variant="h6">
                                        {library.downloadCount.toLocaleString()}
                                    </Typography>
                                </Box>

                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        평점
                                    </Typography>
                                    <Typography variant="h6">
                                        ⭐ {library.ratingAverage.toFixed(1)} ({library.ratingCount}개)
                                    </Typography>
                                </Box>

                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* 노출 설정 */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                노출 설정
                            </Typography>

                            <Box display="flex" flexDirection="column" gap={2}>
                                <Box display="flex" alignItems="center" gap={1}>
                                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 80 }}>
                                        노출 중
                                    </Typography>
                                    <Chip
                                        label={library.isVisible ? '예' : '아니오'}
                                        color={library.isVisible ? 'success' : 'default'}
                                        size="small"
                                    />
                                </Box>

                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        공개일
                                    </Typography>
                                    <Typography variant="body1">
                                        {library.publishedAt
                                            ? formatKstDateTime(library.publishedAt)
                                            : '-'}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* 날짜 정보 */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                날짜 정보
                            </Typography>

                            <Box display="flex" flexDirection="column" gap={2}>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        생성일
                                    </Typography>
                                    <Typography variant="body1">
                                        {formatKstDateTime(library.createdAt)}
                                    </Typography>
                                </Box>

                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        수정일
                                    </Typography>
                                    <Typography variant="body1">
                                        {formatKstDateTime(library.updatedAt)}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* 미리보기 단어 */}
                <Grid size={{ xs: 12 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                미리보기 단어
                            </Typography>

                            {library.previewWords && library.previewWords.length > 0 ? (
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>단어</TableCell>
                                            <TableCell>의미</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {library.previewWords.map((word: PreviewWord, index: number) => (
                                            <TableRow key={index}>
                                                <TableCell>{word.word}</TableCell>
                                                <TableCell>{word.meaning}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            ) : (
                                <Typography color="text.secondary">미리보기 단어가 없습니다.</Typography>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* 그룹 내 단어장 */}
                {library.itemType === LibraryItemType.GROUP && library.groupWordbooks && (
                    <Grid size={{ xs: 12 }}>
                        <Card>
                            <CardContent>
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                    <Typography variant="h6">
                                        포함된 단어장 ({library.groupWordbooks.length}권)
                                    </Typography>
                                </Box>
                                <Divider sx={{ mb: 2 }} />

                                {library.groupWordbooks.length === 0 ? (
                                    <Typography color="text.secondary" textAlign="center" py={4}>
                                        포함된 단어장이 없습니다.
                                    </Typography>
                                ) : (
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell width={80}>썸네일</TableCell>
                                                <TableCell>제목</TableCell>
                                                <TableCell align="center">단어 수</TableCell>
                                                <TableCell align="center">액션</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {library.groupWordbooks.map((wb: GroupWordbookInfo) => (
                                                <TableRow
                                                    key={wb.wordbookId}
                                                    hover
                                                    sx={{ cursor: 'pointer' }}
                                                    onClick={() => navigate(`/wordbook/${wb.wordbookId}`)}
                                                >
                                                    <TableCell onClick={(e) => e.stopPropagation()}>
                                                        {wb.thumbnailUrl ? (
                                                            <Box
                                                                component="img"
                                                                src={wb.thumbnailUrl}
                                                                alt={wb.title}
                                                                sx={{
                                                                    width: 48,
                                                                    height: 48,
                                                                    borderRadius: 1,
                                                                    objectFit: 'cover',
                                                                }}
                                                            />
                                                        ) : (
                                                            <Box
                                                                sx={{
                                                                    width: 48,
                                                                    height: 48,
                                                                    borderRadius: 1,
                                                                    border: '2px dashed',
                                                                    borderColor: 'divider',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    bgcolor: 'grey.50',
                                                                    cursor: 'pointer',
                                                                    '&:hover': {
                                                                        borderColor: 'primary.main',
                                                                        bgcolor: 'primary.50',
                                                                    },
                                                                }}
                                                                component="label"
                                                            >
                                                                {uploadingWordbookId === wb.wordbookId ? (
                                                                    <CircularProgress size={20} />
                                                                ) : (
                                                                    <CloudUpload fontSize="small" color="action" />
                                                                )}
                                                                <input
                                                                    type="file"
                                                                    accept="image/*"
                                                                    hidden
                                                                    onChange={(e) => {
                                                                        const file = e.target.files?.[0];
                                                                        if (file) {
                                                                            handleWordbookThumbnailUpload(wb.wordbookId, file);
                                                                        }
                                                                        e.target.value = '';
                                                                    }}
                                                                />
                                                            </Box>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" fontWeight={500}>
                                                            {wb.title}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Chip
                                                            label={wb.wordCount.toLocaleString()}
                                                            size="small"
                                                            color={wb.wordCount > 0 ? 'primary' : 'default'}
                                                        />
                                                    </TableCell>
                                                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => navigate(`/wordbook/${wb.wordbookId}`)}
                                                            title="상세보기"
                                                        >
                                                            <MenuBook fontSize="small" />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                )}
            </Grid>
        </Box>
    );
};
